import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Card,
  CardContent,
  Stack,
  Avatar,
  Collapse,
  Autocomplete,
} from "@mui/material"
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  Comment as CommentIcon,
  SwapHoriz as SwapHorizIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Work as WorkIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material"
import { useApiQuery, useApiMutation } from "../../hooks/useApi"
import { Link } from "react-router-dom"
import { QueryClient } from "@tanstack/react-query"
import * as XLSX from "xlsx"

function ExceededDueDateJobsPage() {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [searchTerm, setSearchTerm] = useState("")

  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedJob, setSelectedJob] = useState(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [addCommentDialogOpen, setAddCommentDialogOpen] = useState(false)

  const [editData, setEditData] = useState({
    JobId: "",
    fees: "",
    dueDate: "",
    assignedTo: "",
    status: "",
    jobTypeId: ""
  })
  const [comment, setComment] = useState("")

  const { data: jobs, isLoading, refetch } = useApiQuery(["jobs"], "/jobs/due-date/exceeded")
  const { data: users } = useApiQuery(["users"], "/users")
  const { data: jobList } = useApiQuery(["jobTypes"], "/job-list")

  const updateJobMutation = useApiMutation(`/jobs/update/${editData.JobId}`, "PATCH", {
    onSuccess: () => {
      setEditDialogOpen(false)
      refetch()
    },
  })

  const setJobCommentMutation = useApiMutation(`/jobs/${selectedJob?.JobId}/comments`, "POST", {
    onSuccess: () => {
      setAddCommentDialogOpen(false)
      setComment("")
      QueryClient.invalidateQueries({ queryKey: "jobs" })
      setAnchorEl(null)
    }
  })

  const handleChangePage = (event, newPage) => setPage(newPage)

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleExportToExcel = () => {
    const allJobs = filteredJobs

    // Prepare data for Excel
    const excelData = allJobs.map((job) => ({
      "Job ID": job.JobId,
      "Client Name": job.clientName,
      "Job Type": job.jobType,
      "Due Date": job.dueDate ? new Date(job.dueDate).toLocaleDateString() : "N/A",
      "Status": job.status,
      "Sub Status": job.subStatus || "N/A",
      "Assigned To": job.assignedToName,
      "Assigned Role": job.assignedToRole || "N/A",
      "Fees (₹)": job.fees || 0,
      "Created At": new Date(job.createdAt).toLocaleDateString(),
      "Description": job.description || "N/A",
    }))

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Exceeded Jobs")

    // Set column widths
    const columnWidths = [
      { wch: 15 }, // Job ID
      { wch: 20 }, // Client Name
      { wch: 25 }, // Job Type
      { wch: 12 }, // Due Date
      { wch: 12 }, // Status
      { wch: 20 }, // Sub Status
      { wch: 20 }, // Assigned To
      { wch: 15 }, // Assigned Role
      { wch: 12 }, // Fees
      { wch: 15 }, // Created At
      { wch: 30 }, // Description
    ]
    worksheet["!cols"] = columnWidths

    // Generate filename with current date
    const fileName = `Exceeded_Jobs_${new Date().toISOString().split('T')[0]}.xlsx`

    // Export the file
    XLSX.writeFile(workbook, fileName)
  }

  const handleMenuClick = (event, job) => {
    setAnchorEl(event.currentTarget)
    setSelectedJob(job)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleViewClick = () => {
    setViewDialogOpen(true)
    handleMenuClose()
  }

  const handleEditClick = () => {
    if (!selectedJob) return

    // console.log('hanle edit click selectedJob', selectedJob)

    setEditData({
      JobId: selectedJob.JobId,
      fees: selectedJob.fees || "",
      dueDate: selectedJob.dueDate ? new Date(selectedJob.dueDate).toISOString().split("T")[0] : "",
      assignedTo: selectedJob.assignedToId || "",
      status: selectedJob.status || "",
      jobTypeId: selectedJob.jobTypeId || "",
    })
    setEditDialogOpen(true)
    handleMenuClose()
  }

  const handleEditChange = (field, value) => {
    // console.log('handleEditChange EditData', editData)
    setEditData((prev) => ({ ...prev, [field]: value }))
  }

  const handleEditSave = async () => {
    try {
      if (!editData.JobId) {
        console.error("No job ID available for update")
        return
      }
      // console.log('handleEditSave', editData);
      await updateJobMutation.mutateAsync({
        data: {
          fees: editData.fees,
          dueDate: editData.dueDate,
          assignedTo: editData.assignedTo,
          status: editData.status,
          jobTypeId: editData.jobTypeId,
        },
      })
    } catch (error) {
      console.error("Failed to update job:", error)
    }
  }

  const handleDialogClose = () => {
    setEditDialogOpen(false)
    setSelectedJob(null)
  }

  const handleViewDialogClose = () => {
    setViewDialogOpen(false)
    setSelectedJob(null)
  }

  const handleAddCommentClick = () => {
    setAddCommentDialogOpen(true)
    handleMenuClose()
  }

  const handleAddCommentSave = async () => {
    try {
      if (!comment) return
      const data = { commentText: comment }
      await setJobCommentMutation.mutateAsync(data)
    } catch (error) {
      //console.log('Failed to add comment ', error)
    }
  }

  const getStatusColor = (status) => {
    const statusColors = {
      completed: "success",
      processing: "primary",
      new: "warning",
      cancelled: "error",
      closed: "default",
    }
    return statusColors[status] || "default"
  }

  const getSubStatusLabel = (subStatus) => {
    const labels = {
      documentsPending: "Documents Pending",
      taxPending: "Tax Pending",
      verificationPending: "Verification Pending",
      clientNotResponding: "Client Not Responding",
      keptOnHold: "Kept On Hold"
    }
    return labels[subStatus] || subStatus
  }

  const getStatusChangeIcon = (type) => {
    if (type === "status") return <TrendingUpIcon fontSize="small" />
    if (type === "subStatus") return <SwapHorizIcon fontSize="small" />
    return <CommentIcon fontSize="small" />
  }

  const getAvatarColor = (type, to) => {
    if (type === "status") {
      if (to === "completed") return "#4caf50"
      if (to === "processing") return "#2196f3"
      if (to === "cancelled") return "#f44336"
      return "#ff9800"
    }
    return "#9e9e9e"
  }

  const formatStatusChange = (entry) => {
    const fromText = entry.from || "None"
    const toText = entry.to

    if (entry.type === "subStatus") {
      return {
        from: getSubStatusLabel(fromText),
        to: getSubStatusLabel(toText)
      }
    }
    return {
      from: fromText.charAt(0).toUpperCase() + fromText.slice(1),
      to: toText.charAt(0).toUpperCase() + toText.slice(1)
    }
  }

  if (isLoading) return <Typography>Loading...</Typography>

  const allJobs = Array.isArray(jobs) ? jobs : []

  const filteredJobs = allJobs.filter((job) => {
    const s = searchTerm.toLowerCase()
    return (
      !searchTerm ||
      job.JobId?.toLowerCase().includes(s) ||
      job.clientName?.toLowerCase().includes(s) ||
      (job.assignedToName && job.assignedToName.toLowerCase().includes(s))
    )
  })

  const paginatedJobs = filteredJobs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" component="h1">
          Exceeded Jobs Management
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportToExcel}
            disabled={filteredJobs.length === 0}
          >
            Export to Excel
          </Button>
        </Box>
      </Box>

      {/* Search bar */}
      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          placeholder="Search by Job ID, client name or assignee..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setPage(0)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          fullWidth
          size="small"
        />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Job ID</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Job Type</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sub Status</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedJobs.length > 0 ? (
              paginatedJobs.map((job) => (
                <TableRow key={job.JobId} hover>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>
                      {job.JobId}
                    </Typography>
                  </TableCell>
                  <TableCell>{job.clientName}</TableCell>
                  <TableCell>{job.jobType}</TableCell>
                  <TableCell>
                    <Chip label={new Date(job.dueDate)?.toLocaleDateString()} color={getStatusColor("cancelled")} size="small" />
                  </TableCell>

                  <TableCell>
                    <Chip label={job.status} color={getStatusColor(job.status)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={job.subStatus || 'N/A'} color={getStatusColor(job.status)} size="small" />
                  </TableCell>
                  <TableCell>{job.assignedToName}</TableCell>
                  <TableCell>{new Date(job.createdAt)?.toLocaleDateString()}</TableCell>
                  <TableCell>
                    <IconButton onClick={(e) => handleMenuClick(e, job)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No jobs found matching the selected filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[20, 40, 60]}
          component="div"
          count={filteredJobs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewClick}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditClick}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Job
        </MenuItem>
        <MenuItem onClick={() => handleAddCommentClick()}>
          <CommentIcon sx={{ mr: 1 }} />
          Add comment
        </MenuItem>
      </Menu>

      {/* Add Comment Dialog */}
      <Dialog open={addCommentDialogOpen} onClose={() => setAddCommentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.JobId} - {selectedJob.jobType}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Client: {selectedJob.clientName}
              </Typography>
              <TextField
                fullWidth
                label="Comments"
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add notes about the update..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCommentDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCommentSave} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleViewDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" component="div">
            Job Details
          </Typography>
          <IconButton onClick={handleViewDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {selectedJob && (
            <Box>
              {/* Job Overview Card */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Grid container spacing={1}>
                    {/* Row 1: Job ID, Client, Job Type */}
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Job ID</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {selectedJob.JobId}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Client</Typography>
                      <Typography variant="body2">
                        {selectedJob.clientName || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Job Type</Typography>
                      <Typography variant="body2">
                        {selectedJob.jobType || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedJob.assignedToName}
                        <Chip
                          label={selectedJob.assignedToRole}
                          size="small"
                          sx={{ ml: 1, height: 20 }}
                        />
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Due Date</Typography>
                      <Typography variant="body2">
                        {selectedJob.dueDate
                          ? new Date(selectedJob.dueDate).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })
                          : "N/A"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Fees</Typography>
                      <Typography variant="body2">
                        ₹{selectedJob.fees?.toLocaleString('en-IN') || 0}
                      </Typography>
                    </Grid>

                    {/* Row 3: Status + Substatus */}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Box sx={{ mt: 0.3 }}>
                        <Chip
                          label={selectedJob.status}
                          color={getStatusColor(selectedJob.status)}
                          size="small"
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Sub Status</Typography>
                      <Box sx={{ mt: 0.3 }}>
                        {selectedJob.subStatus ? (
                          <Chip
                            label={selectedJob.subStatus}
                            color={getStatusColor(selectedJob.status)}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">None</Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Created At */}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Created At</Typography>
                      <Typography variant="body2">
                        {new Date(selectedJob.createdAt).toLocaleString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Grid>

                    {/* Description (optional) */}
                    {selectedJob.description && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Description</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedJob.description}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Status History & Comments */}
              <Card variant="outlined">
                <CardContent sx={{ pb: 1 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <CommentIcon fontSize="small" />
                    Status History & Comments
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {/* General Comments Section */}
                  {selectedJob.comments && selectedJob.comments.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="primary"
                        gutterBottom
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        Comments ({selectedJob.comments.length})
                      </Typography>
                      <Stack spacing={1}>
                        {selectedJob.comments.map((comment, idx) => (
                          <Paper
                            key={idx}
                            elevation={0}
                            sx={{
                              p: 1.2,
                              bgcolor: "action.hover",
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "baseline",
                                mb: 0.5,
                              }}
                            >
                              <Typography variant="body2" fontWeight={500}>
                                {comment.commentedBy?.name || "Unknown User"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ whiteSpace: "nowrap", ml: 1 }}
                              >
                                {new Date(comment.commentedAt).toLocaleString("en-IN", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.2,
                                lineHeight: 1.3,
                              }}
                            >
                              {comment.commentText}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                      <Divider sx={{ my: 2 }} />
                    </Box>
                  )}

                  {/* Status History Timeline */}
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                  >
                    Status Change History
                  </Typography>

                  {selectedJob.statusHistory && selectedJob.statusHistory.length > 0 ? (
                    <Stack spacing={1.5}>
                      {selectedJob.statusHistory.map((entry, index) => {
                        const statusChange = formatStatusChange(entry)
                        return (
                          <Box
                            key={entry._id || index}
                            sx={{
                              display: "flex",
                              gap: 1.5,
                              position: "relative",
                              "&:not(:last-child)::after": {
                                content: '""',
                                position: "absolute",
                                left: "16px",
                                top: "36px",
                                bottom: "-16px",
                                width: "1.5px",
                                bgcolor: "divider",
                              },
                            }}
                          >
                            {/* Smaller Avatar */}
                            <Avatar
                              sx={{
                                bgcolor: getAvatarColor(entry.type, entry.to),
                                width: 32,
                                height: 32,
                                flexShrink: 0,
                              }}
                            >
                              {getStatusChangeIcon(entry.type)}
                            </Avatar>

                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "baseline",
                                  mb: 0.3,
                                }}
                              >
                                <Box sx={{ mar: "5px" }}>
                                  <Chip
                                    label={entry.type === "status" ? "Status" : "Sub-Status"}
                                    size="small"
                                    color={entry.type === "status" ? "primary" : "default"}
                                    sx={{
                                      fontSize: "0.7rem",
                                      height: 20,
                                      fontWeight: 500,
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary">By: {entry.by.name} </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(entry.at).toLocaleString("en-IN", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </Typography>
                              </Box>

                              <Paper
                                elevation={0}
                                sx={{
                                  p: 1,
                                  bgcolor: "action.hover",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    mb: entry.reason ? 0.8 : 0,
                                  }}
                                >
                                  <Chip
                                    label={statusChange.from}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: "0.7rem", height: 20 }}
                                  />
                                  <ArrowForwardIcon fontSize="small" sx={{ fontSize: "1rem" }} />
                                  <Chip
                                    label={statusChange.to}
                                    size="small"
                                    color={
                                      entry.type === "status"
                                        ? getStatusColor(entry.to)
                                        : "default"
                                    }
                                    sx={{ fontSize: "0.7rem", height: 20 }}
                                  />
                                </Box>

                                {entry.reason && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      mt: 0.3,
                                      color: "text.secondary",
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    "{entry.reason}"
                                  </Typography>
                                )}
                              </Paper>
                            </Box>
                          </Box>
                        )
                      })}
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 2 }}
                    >
                      No status changes recorded yet
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleViewDialogClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Job Dialog */}
      <Dialog open={editDialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Job Details</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Fees"
              type="number"
              value={editData.fees}
              onChange={(e) => handleEditChange("fees", e.target.value)}
              fullWidth
            />
            <TextField
              label="Due Date"
              type="date"
              value={editData.dueDate}
              onChange={(e) => handleEditChange("dueDate", e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
            />
            <FormControl fullWidth>
              <InputLabel>Job Type</InputLabel>
              <Select
                value={editData.jobTypeId}
                onChange={(e) => handleEditChange("jobTypeId", e.target.value)}
                label="Job Type"
              >
                {jobList?.map((job) => (
                  <MenuItem key={job._id} value={job._id}>
                    {job.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={editData.assignedTo}
                onChange={(e) => handleEditChange("assignedTo", e.target.value)}
                label="Assigned To"
              >
                {users?.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ExceededDueDateJobsPage